import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import ArrowDown from 'baseui/icon/arrow-down';
import ArrowUp from 'baseui/icon/arrow-up';
import stylesColors from 'common/styles/styles.colors';
import EmptyView from 'components/common/empty-view';
import LoadingComponent from 'components/common/loading';
import { Url } from 'next/dist/shared/lib/router/router';
import Link from 'next/link';
import * as React from 'react';

import {
  HeaderContainer,
  NoDataContainer,
  ResizeBorder,
  SortContainer,
  SpanText,
  StickyTD,
  StickyTHead,
  StickyTopTR,
  StyledTable,
  TBody,
  Td,
  Th,
  Tr,
} from '../table/components/table';
import TableContainer from '../table/components/table-container';
import Text from '../text';

export type IColumn<T> = ColumnDef<T> & {
  sortName?: string;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  noPadding?: boolean;
  textAlign?: 'left' | 'right' | 'center' | 'justify' | 'initial' | 'inherit';
  isNumber?: boolean;
  isHidden?: boolean;
};

interface RowProps<T> {
  row?: any;
  onRowClick?: (value: { original: T; index: number }) => void;
  showBorder?: boolean;
  onOpenDetail?: (value: { original: T; index: number }) => void;
  rowLink?: (value: { original: T; index: number }) => Url | undefined;
}

export interface TableComponentProps<T> extends Omit<RowProps<T>, 'row'> {
  data: T[];
  isLoading?: boolean;
  columns: ColumnDef<any>[];
  renderRowSubComponent?: ({ row }) => React.ReactNode;
  headerColor?: string;
  showContainerBorder?: boolean;
  onRenderAdditionalComponent?: React.ReactNode;
  isShowRawTable?: boolean;
}

function Row<T>(props: RowProps<T>) {
  const [isHover, setState] = React.useState(false);
  const rowLink = props.rowLink?.({
    original: props.row.original,
    index: props.row.index,
  });
  const row = (
    <Tr
      onMouseEnter={() => setState(true)}
      onMouseLeave={() => setState(false)}
      onClick={(e) => {
        props.onRowClick &&
          props.onRowClick({
            original: props.row.original,
            index: props.row.index,
          });
        if (props.onOpenDetail && e.detail === 2) {
          props.onOpenDetail({
            original: props.row.original,
            index: props.row.index,
          });
          return e.stopPropagation();
        }
      }}
      style={{
        cursor: props.onRowClick ? 'pointer' : 'default',
        backgroundColor: isHover ? stylesColors.primaryLightest : '#FFFFFF',
      }}
    >
      {props.row?.getVisibleCells().map((cell, cellIdx) => {
        const cellColumn: any = cell.column.columnDef;
        const {
          textAlign: _textAlign,
          isNumber,
          maxSize,
          minSize,
        }: any = cellColumn;
        const textAlign = isNumber ? 'right' : _textAlign;
        const content = (
          <SpanText style={{ textAlign }}>
            {flexRender(cellColumn.cell, cell.getContext())}
          </SpanText>
        );
        if (cellColumn.stickyLeft || cellColumn.stickyRight) {
          return (
            <StickyTD
              key={cell.id}
              showRightBorder={props.showBorder}
              style={{
                width: cell.column.getSize(),
                minWidth: minSize,
                maxWidth: maxSize,
                ...(cellColumn.noPadding ? { padding: 0 } : {}),
                [`${cellColumn.stickyLeft ? 'left' : 'right'}`]: 0,
                ...(isHover && {
                  backgroundColor: stylesColors.primaryLightest,
                }),
              }}
            >
              {content}
            </StickyTD>
          );
        }

        return (
          <Td
            key={cell.id}
            showRightBorder={props.showBorder}
            style={{
              ...(cellColumn.noPadding ? { padding: 0 } : {}),
              width: cell.column.getSize(),
              minWidth: minSize,
              maxWidth: maxSize,
              ...(isHover && {
                backgroundColor: stylesColors.primaryLightest,
              }),
            }}
          >
            {content}
          </Td>
        );
      })}
    </Tr>
  );
  if (rowLink) {
    return (
      <Link
        href={rowLink}
        passHref
        style={{
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        {row}
      </Link>
    );
  }
  return row;
}

export default function TableComponent<T>(props: TableComponentProps<T>) {
  const {
    isLoading,
    columns,
    data: _data,
    renderRowSubComponent,
    headerColor,
    showBorder,
    showContainerBorder,
    onRenderAdditionalComponent,
    onOpenDetail,
  } = props;

  const data: any = _data;

  const table = useReactTable({
    data,
    columns,
    getSubRows: (row) => row.subRows,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const { getRowModel, getHeaderGroups } = table;
  const rows = getRowModel().rows;
  const headerGroups = getHeaderGroups();

  const _noData = React.useMemo(() => !rows.length, [rows.length]);

  const tableComponentRaw = React.useMemo(() => {
    return (
      <>
        <StyledTable
          style={{
            width: table.getTotalSize(),
          }}
        >
          <StickyTHead>
            {headerGroups?.map((headerGroup) => (
              <StickyTopTR key={headerGroup.id}>
                {headerGroup.headers.map((header, columnIdx) => {
                  const column: any = header.column.columnDef;
                  return (
                    <Th
                      key={`${headerGroup.id}-${column.header}-${columnIdx}`}
                      showRightBorder
                      {...{
                        style: {
                          position:
                            column.stickyRight || column.stickyLeft
                              ? 'sticky'
                              : 'relative',
                          width: header.getSize(),
                          maxWidth: column.maxSize,
                          minWidth: column.minSize,
                          cursor: column.sortName ? 'pointer' : 'default',
                          ...((column.stickyRight || column.stickyLeft) && {
                            [`${column.stickyLeft ? 'left' : 'right'}`]: 0,
                            zIndex: 100,
                          }),
                          background: headerColor,
                        },
                      }}
                    >
                      <HeaderContainer
                        onClick={() => {
                          column.sortName &&
                            column.getToggleSortingHandler &&
                            column.getToggleSortingHandler(column);
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          maxWidth: column.maxSize,
                          minWidth: column.minSize,
                          cursor: column.sortName ? 'pointer' : 'default',
                        }}
                      >
                        <Text variant="captionSemibold">
                          {flexRender(column.header, header.getContext())}
                        </Text>
                        {column.isSorted ? (
                          <SortContainer>
                            {column.isSortedDesc ? (
                              <ArrowDown size={20} />
                            ) : (
                              <ArrowUp size={20} />
                            )}
                          </SortContainer>
                        ) : null}
                      </HeaderContainer>
                      <ResizeBorder
                        {...{
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `resizer ${
                            header.column.getIsResizing() ? 'isResizing' : ''
                          }`,
                        }}
                      />
                    </Th>
                  );
                })}
              </StickyTopTR>
            ))}
          </StickyTHead>
          <TBody>
            {!_noData && (
              <>
                {rows.length ? (
                  <>
                    {rows?.map((row, i) => {
                      return (
                        <React.Fragment key={i}>
                          <Row
                            row={row}
                            onOpenDetail={onOpenDetail}
                            onRowClick={props.onRowClick}
                            showBorder={showBorder}
                            rowLink={props.rowLink}
                          />
                          {row.getIsExpanded() &&
                            renderRowSubComponent &&
                            renderRowSubComponent({ row })}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
            {onRenderAdditionalComponent}
          </TBody>
        </StyledTable>
        {!onRenderAdditionalComponent && _noData && (
          <NoDataContainer>
            {isLoading ? <LoadingComponent /> : <EmptyView />}
          </NoDataContainer>
        )}
      </>
    );
  }, [
    _noData,
    headerColor,
    headerGroups,
    isLoading,
    onOpenDetail,
    onRenderAdditionalComponent,
    props.onRowClick,
    props.rowLink,
    renderRowSubComponent,
    rows,
    showBorder,
    table,
  ]);

  if (props.isShowRawTable) return tableComponentRaw;

  return (
    <TableContainer
      className="table-component"
      showBorder={showContainerBorder}
    >
      <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
        {tableComponentRaw}
      </div>
    </TableContainer>
  );
}
